import React, { useMemo } from 'react';
import { styled } from 'styled-components';
import Hyperlink from '@ingka/hyperlink';
import Text from '@ingka/text';
import informationCircle from '@ingka/ssr-icon/paths/information-circle';
import InlineMessage from '@ingka/inline-message';
import { getLoginLink } from '../helpers/loginLinkHelper';
import {
  type AvailableService,
  type PriceOfferType,
} from '../services/appointment/model';
import TextWithPrice from './TextWithPrice';
import { isIkeaMembershipSavings } from '../helpers/currencyHelper';
import { useTranslation } from 'react-i18next';
import colors from '../styles/colors';
import LoginPromptCard from './LoginPromptCard';

const StyledText = styled(Text)`
  &&& {
    display: inline-flex;
    margin-top: 0;
    margin-bottom: 2.5rem;
  }
`;

const NeutralInlineMessage = styled(InlineMessage)`
  &&& {
    margin-bottom: 2.5rem;
    svg {
      fill: ${colors.icon.default};
    }
  }
`;

interface LoginLinkProps {
  service?: AvailableService;
  showPromptCard?: boolean;
  showLoginInfoText?: boolean;
}

const getLoginLinkTextKey = (priceOfferType: PriceOfferType): string =>
  priceOfferType === 'family'
    ? 'pages.start.loginLinkTextFamilyPrice'
    : 'pages.start.loginLinkTextBusinessNetworkPrice';

const getLoginLinkIdentifier = (priceOfferType: PriceOfferType): string =>
  priceOfferType === 'family'
    ? 'familyPriceLoginLink'
    : 'businessNetworkPriceLoginLink';

const LoginLink: React.FC<LoginLinkProps> = ({
  service,
  showPromptCard,
  showLoginInfoText,
}) => {
  const { t } = useTranslation();

  const { priceOfferType, inclSavingsFormatted } = service?.displayPrice || {};
  const showMemberPriceLink =
    !!inclSavingsFormatted &&
    !!priceOfferType &&
    isIkeaMembershipSavings(priceOfferType);
  const loginUrl = getLoginLink(
    (window?.location || '').toString(),
    CSS_CONFIG,
  );

  const linkText = useMemo(() => {
    if (!showMemberPriceLink || !priceOfferType) {
      return t('pages.start.loginLinkText');
    }
    return (
      <TextWithPrice
        textKey={getLoginLinkTextKey(priceOfferType)}
        price={inclSavingsFormatted}
      />
    );
  }, [showMemberPriceLink, priceOfferType, inclSavingsFormatted, t]);

  const dataCyIdentifier = useMemo(() => {
    return showMemberPriceLink && priceOfferType
      ? getLoginLinkIdentifier(priceOfferType)
      : 'regularLoginLink';
  }, [showMemberPriceLink, priceOfferType]);

  return (
    <>
      {!showPromptCard && (
        <StyledText tagName="p" textStyle="Body.L" data-cy="login-link">
          <Hyperlink
            id="loginHyperlink"
            url={loginUrl}
            text={linkText}
            colour="black"
            data-cy={dataCyIdentifier}
          />
        </StyledText>
      )}
      {showPromptCard && (
        <LoginPromptCard
          linkTo={loginUrl}
          textWithPrice={showMemberPriceLink ? linkText : null}
          dataCy={dataCyIdentifier}
        />
      )}
      {showLoginInfoText && (
        <NeutralInlineMessage
          subtle
          body={t('pages.start.loginPrompt')}
          ssrIcon={informationCircle}
        />
      )}
    </>
  );
};

export default LoginLink;
